import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { COUNTRIES, DEFAULT_SKILL_CATEGORIES } from '../lib/constants'
import { Modal, FormField, Grid, btnStyle, btnSecStyle } from './CardsPage'


function GymPlanSection({ club }) {
  const [sub, setSub] = useState(null)
  const [loading, setLoading] = useState(true)
  const [subscribing, setSubscribing] = useState(null)

  useEffect(() => {
    if (!club?.id) return
    supabase.from('gym_subscriptions').select('*').eq('club_id', club.id).maybeSingle()
      .then(({ data }) => { setSub(data); setLoading(false) })
  }, [club?.id])

  async function subscribe(plan) {
    setSubscribing(plan)
    const { data, error } = await supabase.functions.invoke('gym-subscribe', {
      body: {
        club_id: club.id,
        plan,
        success_url: window.location.origin + '/app?redirect=settings&sub=success',
        cancel_url: window.location.origin + '/app?redirect=settings',
      }
    })
    if (data?.url) window.location.href = data.url
    else { alert('Error: ' + (error?.message || 'Could not start checkout')); setSubscribing(null) }
  }

  if (loading) return null

  const plans = [
    { id: 'starter', name: 'Starter', price: '€1', desc: 'Test plan' },
    { id: 'standard', name: 'Standard', price: '€49.90', desc: 'For growing gyms' },
    { id: 'pro', name: 'Pro', price: '€79.90', desc: 'Everything unlocked' },
  ]

  return (
    <div style={panelStyle}>
      <div style={panelTitle}>MyAthleteCard plan</div>
      {sub && sub.status === 'active' ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px', background: '#eaf7f0', borderRadius: 10, marginBottom: 16 }}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#1a7040', flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a', textTransform: 'capitalize' }}>{sub.plan} plan — Active</div>
            <div style={{ fontSize: 12, color: '#888' }}>Thanks for being a MyAthleteCard customer!</div>
          </div>
        </div>
      ) : (
        <div style={{ fontSize: 13, color: '#888', marginBottom: 16 }}>
          {sub?.status === 'trialing' ? "You're on a free trial. Choose a plan below to continue after your trial ends." : 'Choose a plan to unlock full access for your gym.'}
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
        {plans.map(p => (
          <div key={p.id} style={{
            border: sub?.plan === p.id && sub?.status === 'active' ? '2px solid #1860a8' : '1px solid #eee',
            borderRadius: 10, padding: '14px', textAlign: 'center'
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a', marginBottom: 4 }}>{p.name}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#1860a8', marginBottom: 2 }}>{p.price}<span style={{ fontSize: 11, fontWeight: 400, color: '#888' }}>/mo</span></div>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 10 }}>{p.desc}</div>
            <button onClick={() => subscribe(p.id)} disabled={subscribing === p.id || (sub?.plan === p.id && sub?.status === 'active')}
              style={{
                width: '100%', padding: '7px', borderRadius: 8, border: 'none',
                background: sub?.plan === p.id && sub?.status === 'active' ? '#eee' : '#1860a8',
                color: sub?.plan === p.id && sub?.status === 'active' ? '#888' : '#fff',
                fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit'
              }}>
              {sub?.plan === p.id && sub?.status === 'active' ? 'Current plan' : subscribing === p.id ? 'Loading...' : 'Choose plan'}
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}

export default function SettingsPage() {
  const { club, profile } = useAuth()
  const [form, setForm] = useState({ name: '', country: 'HR', logo_url: '' })
  const [categories, setCategories] = useState([])
  const [techniques, setTechniques] = useState([])
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [logoUploading, setLogoUploading] = useState(false)
  const [showAddCat, setShowAddCat] = useState(false)
  const [newCatName, setNewCatName] = useState('')
  const [expandedCat, setExpandedCat] = useState(null)
  const [newTechName, setNewTechName] = useState('')
  const [loading, setLoading] = useState(true)

  const inviteUrl = club ? `${window.location.origin}/join/${club.slug}` : ''

  const load = useCallback(async () => {
    if (!club) return
    setForm({ name: club.name || '', country: club.country || 'HR', logo_url: club.logo_url || '' })
    const [cats, techs] = await Promise.all([
      supabase.from('skill_categories').select('*').eq('club_id', club.id).order('display_order'),
      supabase.from('techniques').select('*').order('display_order'),
    ])
    setCategories(cats.data || [])
    setTechniques(techs.data || [])
    setLoading(false)
  }, [club])

  useEffect(() => { load() }, [load])

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  async function saveGeneral() {
    setSaving(true)
    await supabase.from('clubs').update({
      name: form.name,
      country: form.country,
      logo_url: form.logo_url,
    }).eq('id', club.id)
    setSaving(false)
    setSaved(true)
    setTimeout(() => setSaved(false), 3000)
  }

  async function uploadLogo(e) {
    const file = e.target.files[0]
    if (!file) return
    if (!file.type.startsWith('image/')) { alert('Please select an image'); return }
    if (file.size > 2 * 1024 * 1024) { alert('Logo must be under 2MB'); return }

    setLogoUploading(true)
    const ext = file.name.split('.').pop()
    const path = `logos/${club.id}.${ext}`

    const { error } = await supabase.storage.from('fighter-photos').upload(path, file, { upsert: true, contentType: file.type })
    if (!error) {
      const { data } = supabase.storage.from('fighter-photos').getPublicUrl(path)
      const url = data.publicUrl + '?t=' + Date.now()
      set('logo_url', url)
      await supabase.from('clubs').update({ logo_url: url }).eq('id', club.id)
    }
    setLogoUploading(false)
  }

  async function addCategory() {
    if (!newCatName.trim()) return
    const { data } = await supabase.from('skill_categories').insert({
      club_id: club.id, name: newCatName.trim(), display_order: categories.length
    }).select().single()
    setCategories(prev => [...prev, data])
    setNewCatName('')
    setShowAddCat(false)
  }

  async function deleteCategory(id) {
    if (!window.confirm('Delete this category and all its techniques?')) return
    await supabase.from('skill_categories').delete().eq('id', id)
    setCategories(prev => prev.filter(c => c.id !== id))
    setTechniques(prev => prev.filter(t => t.category_id !== id))
  }

  async function addTechnique(catId) {
    if (!newTechName.trim()) return
    const catTechs = techniques.filter(t => t.category_id === catId)
    const { data } = await supabase.from('techniques').insert({
      category_id: catId, name: newTechName.trim(), display_order: catTechs.length
    }).select().single()
    setTechniques(prev => [...prev, data])
    setNewTechName('')
  }

  async function deleteTechnique(id) {
    await supabase.from('techniques').delete().eq('id', id)
    setTechniques(prev => prev.filter(t => t.id !== id))
  }

  function copyInviteLink() {
    navigator.clipboard.writeText(inviteUrl)
    alert('Invite link copied!')
  }

  if (loading) return <div style={{ padding: '2rem', color: '#888' }}>Loading...</div>

  return (
    <div style={{ maxWidth: 640 }}>

      <GymPlanSection club={club} />

      {/* General settings */}
      <div style={panelStyle}>
        <div style={panelTitle}>Gym details</div>

        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: '1.25rem', padding: '1rem', background: '#f8f9fc', borderRadius: 10 }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%', border: '2px solid #ddd',
            background: '#fff', overflow: 'hidden', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 24, fontWeight: 700, color: '#1860a8'
          }}>
            {form.logo_url
              ? <img src={form.logo_url} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              : (club?.name || '?')[0].toUpperCase()
            }
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a', marginBottom: 4 }}>Club logo</div>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>Appears on every fighter card as the club badge</div>
            <label style={{
              display: 'inline-block', background: '#1860a8', color: '#fff',
              borderRadius: 8, padding: '6px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 500
            }}>
              {logoUploading ? 'Uploading...' : form.logo_url ? 'Change logo' : 'Upload logo'}
              <input type="file" accept="image/*" style={{ display: 'none' }} onChange={uploadLogo} disabled={logoUploading} />
            </label>
          </div>
        </div>

        <Grid>
          <FormField label="Gym name" fullWidth>
            <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Zagreb BJJ Academy" style={{ width: '100%' }} />
          </FormField>
          <FormField label="Country">
            <select value={form.country} onChange={e => set('country', e.target.value)} style={{ width: '100%' }}>
              {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
          </FormField>
        </Grid>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button onClick={saveGeneral} disabled={saving} style={btnStyle}>
            {saving ? 'Saving...' : 'Save changes'}
          </button>
          {saved && <span style={{ fontSize: 13, color: '#1a7040' }}>✓ Saved</span>}
        </div>
      </div>

      {/* Invite & academy QR */}
      <div style={panelStyle}>
        <div style={panelTitle}>Invite & academy QR</div>
        <p style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
          Share the invite link or print the QR code and stick it on the gym wall. New fighters scan it to sign up automatically.
        </p>
        <div style={{ display: 'flex', gap: 8, marginBottom: '1.25rem' }}>
          <input readOnly value={inviteUrl} style={{ flex: 1, background: '#f8f9fc', color: '#444', fontSize: 12 }} onClick={e => e.target.select()} />
          <button onClick={copyInviteLink} style={btnStyle}>Copy</button>
        </div>

        {/* Invite code */}
        <InviteCodeSection clubId={club?.id} />

        {/* PIN login QR */}
        <div style={{ background: '#f8f9fc', borderRadius: 10, padding: '1rem', display: 'flex', gap: '1.25rem', alignItems: 'center', flexWrap: 'wrap', marginTop: '1rem' }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: 10, border: '1px solid #eee' }}>
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(window.location.origin + '/pin/' + club?.slug)}&bgcolor=ffffff&color=1a1a1a&margin=1`}
              alt="Academy QR" width={120} height={120} style={{ display: 'block', borderRadius: 6 }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a', marginBottom: 4 }}>Academy PIN login QR</div>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 10, lineHeight: 1.6 }}>
              Print this and stick it on the gym wall. Kids scan it, tap their name, enter their PIN — no email needed.
            </div>
            <button onClick={() => window.open(`https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(window.location.origin + '/pin/' + club?.slug)}&bgcolor=ffffff&color=1a1a1a&margin=2`, '_blank')} style={btnStyle}>
              Download QR
            </button>
          </div>
        </div>
      </div>

      {/* Skill categories */}
      <div style={panelStyle}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
          <div style={{ ...panelTitle, margin: 0, flex: 1 }}>Skill categories</div>
          <button onClick={() => setShowAddCat(true)} style={{ ...btnStyle, padding: '6px 12px', fontSize: 12 }}>+ Add category</button>
        </div>
        <p style={{ fontSize: 12, color: '#888', marginBottom: '1rem' }}>
          These are the skills fighters earn stars in. Customise them for your sport.
        </p>

        {categories.map(cat => {
          const catTechs = techniques.filter(t => t.category_id === cat.id)
          const isOpen = expandedCat === cat.id
          return (
            <div key={cat.id} style={{ border: '1px solid #eee', borderRadius: 10, marginBottom: 8, overflow: 'hidden' }}>
              <div style={{
                display: 'flex', alignItems: 'center', padding: '10px 14px',
                background: isOpen ? '#f0f4ff' : '#fff', cursor: 'pointer'
              }} onClick={() => setExpandedCat(isOpen ? null : cat.id)}>
                <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>{cat.name}</span>
                <span style={{ fontSize: 12, color: '#888', marginRight: 12 }}>{catTechs.length} techniques</span>
                <button onClick={e => { e.stopPropagation(); deleteCategory(cat.id) }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#c00', fontSize: 16, lineHeight: 1, padding: '0 4px' }}>×</button>
                <span style={{ fontSize: 12, color: '#888', marginLeft: 8 }}>{isOpen ? '▲' : '▼'}</span>
              </div>
              {isOpen && (
                <div style={{ padding: '10px 14px', borderTop: '1px solid #eee', background: '#fafbff' }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                    {catTechs.map(t => (
                      <span key={t.id} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        background: '#fff', border: '1px solid #e0e0e0', borderRadius: 20,
                        padding: '3px 10px', fontSize: 12, color: '#333'
                      }}>
                        {t.name}
                        <button onClick={() => deleteTechnique(t.id)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', fontSize: 14, lineHeight: 1, padding: 0 }}>×</button>
                      </span>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      value={newTechName} onChange={e => setNewTechName(e.target.value)}
                      placeholder="Add technique..."
                      onKeyDown={e => e.key === 'Enter' && addTechnique(cat.id)}
                      style={{ flex: 1, fontSize: 12, padding: '6px 10px' }}
                    />
                    <button onClick={() => addTechnique(cat.id)} style={{ ...btnStyle, padding: '6px 12px', fontSize: 12 }}>Add</button>
                  </div>
                </div>
              )}
            </div>
          )
        })}

        {showAddCat && (
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <input value={newCatName} onChange={e => setNewCatName(e.target.value)}
              placeholder="Category name (e.g. Takedowns)"
              onKeyDown={e => e.key === 'Enter' && addCategory()}
              style={{ flex: 1, fontSize: 13 }} autoFocus />
            <button onClick={addCategory} style={btnStyle}>Add</button>
            <button onClick={() => { setShowAddCat(false); setNewCatName('') }} style={btnSecStyle}>Cancel</button>
          </div>
        )}
      </div>

    </div>
  )
}

const panelStyle = {
  background: '#fff', borderRadius: 12, padding: '1.25rem',
  border: '1px solid #eee', marginBottom: '1.25rem'
}

const panelTitle = {
  fontSize: 15, fontWeight: 600, color: '#1a1a1a', marginBottom: '1rem'
}

function InviteCodeSection({ clubId }) {
  const [codes, setCodes] = useState([])
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)

  useEffect(() => {
    if (!clubId) return
    supabase.from('invite_codes').select('*').eq('club_id', clubId).eq('active', true).order('created_at', { ascending: false })
      .then(({ data }) => { setCodes(data || []); setLoading(false) })
  }, [clubId])

  async function generateCode() {
    setGenerating(true)
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
    const code = Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join('')
    const { data } = await supabase.from('invite_codes').insert({ club_id: clubId, code }).select().single()
    if (data) setCodes(c => [data, ...c])
    setGenerating(false)
  }

  async function deactivate(id) {
    await supabase.from('invite_codes').update({ active: false }).eq('id', id)
    setCodes(c => c.filter(x => x.id !== id))
  }

  if (loading) return null

  return (
    <div style={{ background: '#f0f4ff', borderRadius: 10, padding: '12px', marginBottom: 0, border: '1px solid #c8d8f8' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>Invite codes</div>
          <div style={{ fontSize: 11, color: '#666' }}>Fighters enter this code when registering to join your gym</div>
        </div>
        <button onClick={generateCode} disabled={generating}
          style={{ background: '#1860a8', color: '#fff', border: 'none', borderRadius: 7, padding: '5px 12px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
          {generating ? '...' : '+ New code'}
        </button>
      </div>
      {codes.length === 0 ? (
        <div style={{ fontSize: 12, color: '#888' }}>No active codes. Generate one above.</div>
      ) : codes.map(c => (
        <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid #d8e8f8' }}>
          <div style={{ fontFamily: 'monospace', fontSize: 16, fontWeight: 700, color: '#1860a8', letterSpacing: '0.1em', flex: 1 }}>{c.code}</div>
          <div style={{ fontSize: 11, color: '#888' }}>{c.uses_count} uses</div>
          <button onClick={() => { navigator.clipboard.writeText(c.code); alert('Code copied!') }}
            style={{ background: 'transparent', border: '1px solid #c8d8f8', borderRadius: 6, padding: '2px 8px', fontSize: 11, cursor: 'pointer', color: '#1860a8' }}>Copy</button>
          <button onClick={() => deactivate(c.id)}
            style={{ background: 'transparent', border: '1px solid #fcc', borderRadius: 6, padding: '2px 8px', fontSize: 11, cursor: 'pointer', color: '#c00' }}>×</button>
        </div>
      ))}
    </div>
  )
}
